import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../form/Button';
import { CheckCircle } from 'lucide-react';

// Define a basic inline spinner component
const BasicSpinner = ({ className = "w-8 h-8" }: { className?: string }) => (
  <svg
    className={`animate-spin ${className}`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

export function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { session } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleSuccess = async () => {
      setIsProcessing(true);
      setError(null);
      const sessionId = searchParams.get('session_id');

      if (!sessionId) {
        setError('Invalid checkout session ID.');
        setIsProcessing(false);
        navigate('/pricing?error=invalid_session');
        return;
      }

      if (!session?.user) {
        setError('User not authenticated.');
        setIsProcessing(false);
        navigate(`/login?redirect=${encodeURIComponent(`/checkout/success?session_id=${sessionId}`)}`);
        return;
      }

      const user = session.user;

      try {
        // Fetch Checkout Session Details
        console.log(`Fetching session details for: ${sessionId}`);
        const response = await fetch('/api/stripe/get-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
          let errorData = { message: `Failed to fetch session details (${response.status})` };
          try {
            errorData = await response.json();
          } catch (e) { /* Ignore JSON parse error */ }
          throw new Error(errorData.message || `HTTP error ${response.status}`);
        }

        const stripeSession = await response.json();
        console.log('Fetched Stripe Session:', stripeSession);

        // Validate session data
        const sessionMetadata = stripeSession.metadata || stripeSession.checkout_session?.metadata;
        const stripeSubscriptionId = stripeSession.subscription || stripeSession.checkout_session?.subscription;
        const stripeCustomerId = stripeSession.customer || stripeSession.checkout_session?.customer;
        const clientReferenceId = stripeSession.client_reference_id || stripeSession.checkout_session?.client_reference_id;

        if (!sessionMetadata || !clientReferenceId || !stripeSubscriptionId || !stripeCustomerId) {
          console.error('Incomplete session data:', stripeSession);
           throw new Error('Incomplete session data received from Stripe endpoint.');
        }

        // Security Check
        if (clientReferenceId !== user.id) {
          throw new Error('Checkout session user ID does not match logged-in user.');
        }

        const planId = sessionMetadata.planId;
        const licenses = parseInt(sessionMetadata.licenses || '100', 10);

        if (planId !== 'enterprise') {
          throw new Error('Only enterprise plans are supported.');
        }

        console.log(`Processing Enterprise Plan with ${licenses} licenses`);

        // Create the Organization
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .insert({
            name: `${user.email ?? 'My'}'s Organization`,
            owner_user_id: user.id,
              stripe_customer_id: stripeCustomerId,
            billing_email: user.email,
            licenses_purchased: licenses,
            licenses_used: 1 // Creator is the first member
            })
            .select('id')
            .maybeSingle();

          if (orgError || !orgData) throw new Error(`Failed to create organization: ${orgError?.message || 'No data returned'}`);
        const organizationId = orgData.id;
          console.log(`Organization created: ${organizationId}`);

        // Create the Subscription record
          const { data: subData, error: subError } = await supabase
            .from('subscriptions')
            .insert({
            user_id: null, // Organization owns the subscription
              organization_id: organizationId,
              stripe_subscription_id: stripeSubscriptionId,
              stripe_customer_id: stripeCustomerId,
            plan: 'enterprise',
            status: 'active',
            licenses_purchased: licenses,
            licenses_used: 1,
            analysis_limit_per_license: 1000,
            current_period_start: new Date().toISOString(),
            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              cancel_at_period_end: false
            })
            .select('id')
            .maybeSingle();

          if (subError || !subData) {
             console.error('Subscription creation failed after org creation', subError);
          throw new Error(`Failed to create enterprise subscription record: ${subError?.message || 'No data returned'}`);
        }
        console.log(`Subscription created: ${subData.id}`);

        // Create the Organization Member record for the creator
          const { data: memberData, error: memberError } = await supabase
            .from('organization_members')
            .insert({
              organization_id: organizationId,
              user_id: user.id,
              role: 'admin',
            analysis_count: 0,
              joined_at: new Date().toISOString(),
            })
          .select('id')
             .maybeSingle();

          if (memberError || !memberData) throw new Error(`Failed to add user to organization: ${memberError?.message || 'No data returned'}`);
        console.log(`Admin member created: ${memberData.id} for user: ${user.id}`);

        addToast('Enterprise subscription activated successfully!', 'success');
        navigate('/settings/organization');

      } catch (err: any) {
        console.error('Error processing checkout success:', err);
        setError(err.message || 'An error occurred while processing your subscription.');
        addToast(err.message || 'Failed to activate subscription.', 'error');
      } finally {
        setIsProcessing(false);
      }
    };

        handleSuccess();
  }, [searchParams, navigate, addToast, session]);

  // Handle redirect
  const handleRedirect = () => {
    navigate('/settings/organization');
  };

  if (isProcessing) {
  return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <BasicSpinner className="mx-auto w-12 h-12 text-blue-600" />
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Setting up your Enterprise account...
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Please wait while we configure your organization.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <span className="text-red-600 text-xl">✕</span>
              </div>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Setup Failed
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {error}
              </p>
            </div>

            <div className="mt-6">
           <Button
             onClick={() => navigate('/pricing')}
                variant="secondary"
             className="w-full"
           >
                Back to Pricing
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Welcome to Enterprise!
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your enterprise subscription is now active. You can now invite up to 100 team members and analyze resumes at scale.
            </p>
          </div>

          <div className="mt-6">
            <Button
              onClick={handleRedirect}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Set Up Your Organization
           </Button>
          </div>
        </div>
         </div>
    </div>
  );
} 